import { getCategories as getAddPanelCategories } from '#packages/addPanelData';
import { CATEGORIES_ID } from '#packages/addPanelDataConsts';
import constants from '#packages/constants';
import type { EditorAPI } from '#packages/editorAPI';
import { pagesAPI } from '#packages/pages';
import {
  api as platformAPI,
  panelHelpers,
  getMyWidgetsSections,
} from '#packages/platform';
import {
  editorSearch,
  leftBar,
  multilingualActions,
} from '#packages/stateManagement';
import {
  addPanel as addPanelUtils,
  sections as sectionsUtils,
} from '#packages/util';
import { onceDataModeInitialized } from '#packages/wixData';
import { DATA_BINDING } from '@wix/app-definition-ids';
import { newAddPanelAPI } from '#packages/newAddPanelAPI';
import {
  type Feature,
  FEATURE,
  FEATURE_PARAM_VALUES,
  parse,
} from '@wix/deeplink-core';
import {
  check as deeplinkCheck,
  register,
  show as deeplinkShow,
} from '@wix/deeplink-editor';
import type { CompRef } from 'types/documentServices';
import { shouldOpenPanel } from '../utils/openPanelByURL';
import type { AnySectionItem } from '@wix/add-panel-common';
import {
  startAdiToEditorTour,
  startEditorToEditorTour,
  TourNames,
  StartOrigin,
} from '#packages/editorTours';
import type { AssistantOperation } from '@wix/tour-maker';
import { MediaImageStudioMode } from '@wix/media-image-studio-opener';
import { mediaStudioUtils } from '#packages/addPanelInfra';
import { MyBusinessOrigin } from '@wix/editor-platform-host-integration/ui';

type AddPanelCategory = ValueOf<typeof CATEGORIES_ID>;
interface AddPanelLocation {
  categoryId: AddPanelCategory | string;
  sectionId: string | null;
}

const ContentManagerTabIdentifiers =
  FEATURE_PARAM_VALUES[FEATURE.contentManager].tab;
type ContentManagerTab = (typeof ContentManagerTabIdentifiers)[number];

const deeplinkOrigin = 'deeplink';

const categoryGroupsPropName = addPanelUtils.isNewPanelEnabled()
  ? 'groups'
  : 'items';
const sectionIdPropName = addPanelUtils.isNewPanelEnabled()
  ? 'sectionName'
  : 'id';

const toursMap: {
  [key: string]: (editorAPI: EditorAPI) => Promise<AssistantOperation>;
} = {
  [TourNames.ADI_TO_EDITOR]: (editorAPI: EditorAPI) =>
    startAdiToEditorTour(editorAPI, StartOrigin.DeepLink),
  [TourNames.EDITOR_TO_EDITOR]: (editorAPI: EditorAPI) =>
    startEditorToEditorTour(editorAPI, StartOrigin.DeepLink),
};

async function getApplicationAddPanelLocation(
  editorAPI: EditorAPI,
  appDefinitionId: string | null,
): Promise<AddPanelLocation | null> {
  if (!appDefinitionId) {
    return null;
  }

  const addPanelCategories = addPanelUtils.isNewPanelEnabled()
    ? (await newAddPanelAPI.getData()).categories
    : getAddPanelCategories(editorAPI);

  for (const category of addPanelCategories) {
    // prioritize top-level categories over nested sections
    if (category.appDefinitionId === appDefinitionId) {
      return {
        categoryId: category.id,
        sectionId: null,
      };
    }
  }

  for (const category of addPanelCategories) {
    for (const group of category[categoryGroupsPropName]) {
      for (const section of group.sections) {
        if (section.appDefinitionId === appDefinitionId) {
          return {
            categoryId: category.id,
            sectionId: section[sectionIdPropName] || null,
          };
        }

        const sectionHasApplicationItem = section.props?.items?.some(
          (sectionItem: AnySectionItem) =>
            sectionItem.appDefinitionId === appDefinitionId,
        );

        if (sectionHasApplicationItem) {
          return {
            categoryId: category.id,
            sectionId: section[sectionIdPropName] || null,
          };
        }
      }
    }
  }

  const sections = getMyWidgetsSections(editorAPI);
  const sectionExists = sections.some(
    (section) => section.id === appDefinitionId,
  );
  if (sectionExists) {
    return {
      categoryId: CATEGORIES_ID.INSTALLED_APPS,
      sectionId: appDefinitionId,
    };
  }

  return null;
}

async function getComponentAddPanelLocation(
  editorAPI: EditorAPI,
  componentType: string | null,
): Promise<AddPanelLocation | null> {
  // see `santa-editor/packages/rEditor/test/app/deeplink.unit.ts`
  // for current (and, possibly, incomplete) `componentType -> category` mapping
  if (!componentType) {
    return null;
  }

  // overrides for _some_ `componentType`s in cases when automatic guess fails
  switch (componentType) {
    case 'core.components.Image':
    case 'wysiwyg.viewer.components.WPhoto':
      return { categoryId: 'image', sectionId: null };
    case 'wysiwyg.viewer.components.Video':
      return { categoryId: 'media', sectionId: null };
    case 'wysiwyg.viewer.components.SiteButton':
      return { categoryId: 'button', sectionId: null };
    case 'wysiwyg.common.components.rssbutton.viewer.RSSButton':
      return getApplicationAddPanelLocation(
        editorAPI,
        editorAPI.tpa.superApps.consts.NEW_BLOG_APP_DEF_ID,
      );
    case 'wysiwyg.common.components.spotifyplayer.viewer.SpotifyPlayer':
      return getApplicationAddPanelLocation(
        editorAPI,
        editorAPI.tpa.superApps.consts.SPOTIFY.APP_DEF_ID,
      );
  }

  for (const category of getAddPanelCategories(editorAPI)) {
    if (category.appDefinitionId) {
      continue;
    }

    for (const categoryItem of category.items) {
      for (const section of categoryItem.sections) {
        const sectionHasComponentItem = section.props?.items?.some(
          (sectionItem: any) =>
            sectionItem.structure?.componentType === componentType,
        );

        if (sectionHasComponentItem) {
          return {
            categoryId: category.id,
            sectionId: section[sectionIdPropName],
          };
        }
      }
    }
  }

  return null;
}

export const _unittest_export_getComponentAddPanelLocation =
  getComponentAddPanelLocation;

export const _unittest_export_getApplicationAddPanelLocation =
  getApplicationAddPanelLocation;

export async function check(feature: Feature) {
  return deeplinkCheck(feature);
}

export async function show(feature: Feature) {
  return deeplinkShow(feature);
}

export function onPreviewReady(editorAPI: EditorAPI) {
  register({
    [FEATURE.appMarket]: {
      show(feature) {
        const { appDefinitionId } = feature.named;

        editorAPI.panelManager.openPanel('tpa.compPanels.appMarketPanel', {
          urlParams: {
            slug: appDefinitionId || null,
          },
        });
      },
    },
    [FEATURE.privateApps]: {
      show(feature) {
        const { appDefinitionId, version } = feature.named;
        editorAPI.panelManager.openPanel('tpa.compPanels.appMarketPanel', {
          urlParams: {},
          selectedSection: {
            id: 'private-apps',
            props: {
              initialAppDefId: appDefinitionId,
              version,
            },
          },
        });
      },
    },
    [FEATURE.component]: {
      check(feature) {
        const { id: compId, panelName } = feature.named;

        // check if panelName is valid
        if (panelName && !constants.componentPanels[panelName]) {
          return false;
        }

        // check if component exists on a page
        const pageRef = editorAPI.components.getPage({
          id: compId,
          type: 'DESKTOP',
        });
        if (!pageRef) {
          return false;
        }

        return true;
      },
      show(feature) {
        const { id: compId, panelName } = feature.named;

        const compRef: CompRef = { id: compId, type: 'DESKTOP' };

        panelHelpers.navigateAndOpenPanel(editorAPI, compRef, panelName);
      },
    },
    [FEATURE.accessibility]: {
      async show() {
        await platformAPI.accessibility.openAccessibilityWizard(editorAPI);
      },
    },
    [FEATURE.addPanel]: {
      async check(feature) {
        const { appDefinitionId, componentType } = feature.named;

        if (
          appDefinitionId &&
          !(await getApplicationAddPanelLocation(editorAPI, appDefinitionId))
        ) {
          return false;
        }

        if (
          componentType &&
          !(await getComponentAddPanelLocation(editorAPI, componentType))
        ) {
          return false;
        }

        return true;
      },
      async show(feature) {
        const { appDefinitionId, componentType } = feature.named;
        const { categoryId, sectionId } =
          (await getApplicationAddPanelLocation(editorAPI, appDefinitionId)) ||
          (await getComponentAddPanelLocation(editorAPI, componentType)) ||
          {};

        const openAddPanelAction = () => {
          openAddPanel(editorAPI, { categoryId, sectionId });
        };

        openAddPanelAction();
      },
    },
    [FEATURE.addSectionPanel]: {
      check: () => {
        return sectionsUtils.isSectionsEnabled();
      },
      show: () => {
        editorAPI.panelManager.openPanel(
          constants.ROOT_COMPS.LEFTBAR.ADD_SECTION_PANEL_NAME,
          {
            origin: deeplinkOrigin,
          },
        );
      },
    },
    [FEATURE.editorTour]: {
      check: () => {
        return sectionsUtils.isSectionsEnabled();
      },
      show: (feature) => {
        const { tourName } = feature.named;

        toursMap[tourName]?.(editorAPI);
      },
    },
    [FEATURE.pagesPanel]: {
      check(feature) {
        const { appDefinitionId } = feature.named;
        if (!appDefinitionId) {
          return true;
        }
        const pagesData = editorAPI.dsRead.pages.getPagesData();
        const appPages = pagesAPI.appPages.getPages(editorAPI, pagesData);
        return appPages.some(({ id }) => id === appDefinitionId);
      },
      show(feature) {
        const { appDefinitionId } = feature.named;
        if (appDefinitionId) {
          return editorAPI.openPagesPanel({
            selectedTabId: appDefinitionId,
            origin: deeplinkOrigin,
          });
        }
        editorAPI.openPagesPanel({ origin: deeplinkOrigin });
      },
    },
    [FEATURE.viewport]: {
      check(feature) {
        return (
          feature.named.size === 'phone' || feature.named.size === 'desktop'
        );
      },
      show(feature) {
        const isMobile = feature.named.size === 'phone';

        if (isMobile !== editorAPI.isMobileEditor()) {
          editorAPI.setEditorMode(isMobile, 'deeplink');
        }
      },
    },
    [FEATURE.contentManager]: {
      async show(feature) {
        if (isWixDataInstalled(editorAPI)) {
          return onceDataModeInitialized(editorAPI).then(async (dataMode) => {
            if (dataMode) {
              editorAPI.panelManager.openPanel(
                constants.ROOT_COMPS.LEFTBAR.WIX_DATA_PANEL_NAME,
                {
                  selectedTabName: getContentManagerTabName(feature.named.tab),
                },
              );
            } else {
              openAddPanel(
                editorAPI,
                await getApplicationAddPanelLocation(editorAPI, DATA_BINDING),
              );
            }
          });
        }
        openAddPanel(
          editorAPI,
          await getApplicationAddPanelLocation(editorAPI, DATA_BINDING),
        );
      },
    },
    [FEATURE.search]: {
      show() {
        editorAPI.store.dispatch(
          editorSearch.actions.openEditorSearchPanel({
            origin: deeplinkOrigin,
          }),
        );
      },
    },
    [FEATURE.themePanel]: {
      show() {
        editorAPI.panelManager.openPanel(
          constants.ROOT_COMPS.LEFTBAR.DESIGN_PANEL_NAME,
          {
            origin: deeplinkOrigin,
          },
        );
      },
    },
    [FEATURE.photoStudio]: {
      show(feature) {
        editorAPI.store.dispatch(leftBar.actions.unhighlightMenu());

        const { mode, file } = feature.named;
        editorAPI.mediaServices.openMediaStudio(
          {
            initiator: 'editor-deeplink',
            imageFileId: file,
            mode: mode as MediaImageStudioMode,
            isUpload: !(mode || file),
          },
          mediaStudioUtils.createAddImageToStageCallback(
            editorAPI,
            'TODO', // TODO: proper preset_id
            'deepLinkPhotoStudio',
          ),
        );
      },
    },
    [FEATURE.multilingual]: {
      check(feature) {
        return !!feature.named.languageCode;
      },
      show(feature) {
        const { languageCode } = feature.named;
        editorAPI.store.dispatch(
          multilingualActions.switchToSecondaryLanguageByCodeThunk(
            languageCode,
          ),
        );
        const features = editorURL.searchParams.getAll('deeplink');
        features.forEach((feature) => {
          const parsedFeature = parse(feature);
          if (parsedFeature && parsedFeature.type !== FEATURE.multilingual) {
            void deeplinkShow(parsedFeature);
          }
        });
      },
    },
    [FEATURE.myBusiness]: {
      check(feature) {
        return (
          !feature.named.appDefinitionId ||
          editorAPI.dsRead.platform.isAppActive(feature.named.appDefinitionId)
        );
      },
      show(feature) {
        editorAPI.panelManager.openPanel(
          constants.ROOT_COMPS.LEFTBAR.APP_MANAGER_PANEL_NAME,
          {
            selectedAppId: feature.named.appDefinitionId,
            origin: MyBusinessOrigin.Deeplink,
          },
        );
      },
    },
  });

  const editorURL = new URL(window.location.href);

  for (const [key, value] of editorURL.searchParams) {
    if (
      key.toLowerCase() === 'openpanel' &&
      shouldOpenPanel(value, editorAPI)
    ) {
      return;
    }
  }

  let feature;

  try {
    feature = parse(editorURL);
  } catch (e) {
    // TODO: add fedops/bilogger tracking of invalid `&deeplink=` URLs
    console.error(e); // eslint-disable-line no-console
  }

  if (feature) {
    void deeplinkShow(feature);
  }
}

function openAddPanel(
  editorAPI: EditorAPI,
  {
    categoryId,
    sectionId,
    origin,
  }: { categoryId: string; sectionId?: string; origin?: string },
) {
  const options = {
    category: categoryId,
    sectionId,
    origin: origin ?? deeplinkOrigin,
  };
  if (addPanelUtils.isNewPanelEnabled()) {
    editorAPI.panelManager.openPanel(
      constants.ROOT_COMPS.LEFTBAR.NEW_ADD_PANEL_NAME,
      options,
    );
  } else {
    editorAPI.panelManager.openPanel(
      constants.ROOT_COMPS.LEFTBAR.ADD_PANEL_NAME,
      options,
    );
  }
}

function getContentManagerTabName(tabIdentifier: ContentManagerTab) {
  const identifierToNameMap = {
    collections: constants.SUPER_APPS.MENU_PANEL_TAB_NAMES.MANAGE,
    elements: constants.SUPER_APPS.MENU_PANEL_TAB_NAMES.ADD,
    learn: constants.SUPER_APPS.MENU_PANEL_TAB_NAMES.LEARN_MORE,
  };
  return identifierToNameMap[tabIdentifier];
}

function isWixDataInstalled(editorAPI: EditorAPI) {
  return editorAPI.dsRead.platform
    .getInstalledEditorApps()
    .some((app) => app.appDefinitionId === DATA_BINDING);
}
